import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import StructuredData from '@/components/feature/StructuredData';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, articles } from '@/mocks/articles';

const categoryImages: Record<string, string> = {
  'sm-basics': 'https://readdy.ai/api/search-image?query=Educational%20illustration%20of%20knowledge%20and%20learning%20concepts%20with%20warm%20amber%20tones%2C%20soft%20abstract%20shapes%2C%20clean%20modern%20aesthetic%2C%20editorial%20design%2C%20warm%20natural%20lighting&width=800&height=450&seq=cat-basics&orientation=landscape',
  'sm-types': 'https://readdy.ai/api/search-image?query=Abstract%20artistic%20representation%20of%20diversity%20and%20categories%2C%20flowing%20organic%20shapes%20in%20warm%20rose%20and%20terracotta%20colors%2C%20modern%20minimalist%20design%2C%20soft%20gradient%20background&width=800&height=450&seq=cat-types&orientation=landscape',
  'safety-guidelines': 'https://readdy.ai/api/search-image?query=Minimalist%20safety%20and%20protection%20concept%20art%2C%20warm%20sage%20green%20and%20cream%20tones%2C%20abstract%20shield-like%20shapes%2C%20clean%20professional%20aesthetic%2C%20soft%20diffused%20lighting&width=800&height=450&seq=cat-safety&orientation=landscape',
  'practice-guide': 'https://readdy.ai/api/search-image?query=Minimalist%20guide%20and%20compass%20illustration%20in%20warm%20amber%20and%20cream%20tones%2C%20abstract%20directional%20elements%2C%20clean%20modern%20composition%2C%20soft%20natural%20lighting%2C%20editorial%20style&width=800&height=450&seq=cat-practice&orientation=landscape',
  'psychology-relationships': 'https://readdy.ai/api/search-image?query=Abstract%20emotional%20connection%20and%20psychology%20artwork%2C%20warm%20rose%20and%20terracotta%20colors%2C%20flowing%20intertwining%20shapes%2C%20soft%20dreamy%20atmosphere%2C%20editorial%20quality&width=800&height=450&seq=cat-psych&orientation=landscape',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-foreground-950 mb-2">分类未找到</h2>
            <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm cursor-pointer">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const categoryArticles = articles.filter((a) => a.categorySlug === slug);
  const canonicalUrl = typeof window !== 'undefined' ? `${window.location.origin}/category/${slug}` : '';

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${category.name} - SM专业指南`}
        description={category.description}
        keywords={`SM,${category.name},SM知识,SM入门教程`}
        canonicalUrl={canonicalUrl}
      />
      <StructuredData
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: canonicalUrl.replace(`/category/${slug}`, '') },
            { '@type': 'ListItem', position: 2, name: category.name, item: canonicalUrl },
          ],
        }}
      />
      <StructuredData
        type="WebPage"
        data={{
          name: `${category.name} - SM专业指南`,
          description: category.description,
          url: canonicalUrl,
        }}
      />

      <Navbar />
      <main className="pt-16">
        <div className="bg-background-50 border-b border-background-200/60">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <Breadcrumb items={[{ name: '首页', path: '/' }, { name: category.name }]} />
            <div className="flex items-center gap-4 mt-2">
              <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-xl bg-accent-100 text-accent-500 flex-shrink-0">
                <i className={`${category.icon} text-2xl md:text-3xl`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">{category.name}</h1>
                <p className="text-foreground-600 text-sm md:text-base mt-1">{category.description}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-background-100/50 border-b border-background-200/60">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
            <div className="flex items-center gap-2 overflow-x-auto flex-nowrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors cursor-pointer ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50 font-medium'
                      : 'bg-background-50 text-foreground-600 border border-background-200/70 hover:border-primary-300 hover:text-primary-500'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <section className="py-12 md:py-16 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {categoryArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.slug}`}
                  className="group block bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-300 transition-all duration-300 cursor-pointer"
                >
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={categoryImages[slug] || categoryImages['sm-basics']}
                      alt={article.title}
                      title={`${article.title} - SM专业指南`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-xs text-foreground-500">{article.readTime}阅读</span>
                      <span className="text-xs text-foreground-500">{article.date}</span>
                    </div>
                    <h3 className="text-base font-semibold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors leading-snug">
                      {article.title}
                    </h3>
                    <p className="text-foreground-600 text-sm line-clamp-2 leading-relaxed">
                      {article.excerpt}
                    </p>
                    <div className="mt-4 flex items-center gap-1 text-sm text-primary-500 font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                      阅读全文
                      <i className="ri-arrow-right-line text-xs"></i>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}